/**
 * Reminder schedules (T-701).
 *
 * The crux of this file, and the thing most likely to be "simplified" later
 * into a bug that appears twice a year:
 *
 * **A reminder time is a wall clock, never an instant.** "Remind me at 08:00"
 * is an instruction about the user's day. It must still mean 08:00 after the
 * clocks change and after they fly to New York — which is the exact opposite of
 * how an *entry* is stored, where the instant is the truth and the zone is
 * context (ADR-0006). Both are right for their own case, and conflating them
 * produces reminders that arrive an hour early twice a year and five hours
 * early on holiday.
 *
 * Delivery is not here and is not coming to the web. ADR-0004: a static site
 * cannot send a push, and the browser API that would have let a page schedule
 * its own notification never shipped. Schedules are authored and stored now so
 * the native app arrives already knowing when to prompt, and so nothing built
 * here is thrown away.
 */

import { err, invalid, ok, type Result, type ValidationError } from './result.js'
import type { Timestamp, WallClock } from './timestamp.js'
import { epochFromWallClock, wallClockIn } from './timestamp.js'

/**
 * A local time of day. No date, no zone, no offset.
 *
 * That absence is the point: attaching either would make this an instant, and
 * an instant is not what "every morning at eight" means.
 */
export interface TimeOfDay {
  /** 0–23. */
  readonly hour: number
  /** 0–59. */
  readonly minute: number
}

/** ISO-8601 weekday numbering: Monday is 1, Sunday is 7. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7

export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7]

const WEEKDAY_NAMES: Readonly<Record<Weekday, string>> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
}

export function weekdayName(weekday: Weekday): string {
  return WEEKDAY_NAMES[weekday]
}

/** The ISO weekday of a local calendar date. */
export function weekdayOf(wall: WallClock): Weekday {
  // `getUTCDay` counts Sunday as 0; ISO counts it as 7.
  const day = new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay()
  return (day === 0 ? 7 : day) as Weekday
}

/**
 * How often a reminder repeats.
 *
 * A discriminated union rather than a bag of optional fields, so an `interval`
 * schedule has no `weekdays` to read and adding a fourth kind turns every
 * unhandled branch into a compile error.
 */
export type Recurrence =
  | { readonly kind: 'daily'; readonly times: readonly TimeOfDay[] }
  | {
      readonly kind: 'weekly'
      readonly weekdays: readonly Weekday[]
      readonly times: readonly TimeOfDay[]
    }
  /** Every N hours between two times of day — for tracking through a day. */
  | {
      readonly kind: 'interval'
      readonly everyHours: number
      readonly from: TimeOfDay
      readonly to: TimeOfDay
    }

export type RecurrenceKind = Recurrence['kind']

export interface ReminderSchedule {
  readonly id: string
  readonly name: string
  /** The check-in this reminder is for, where it is for one. */
  readonly templateId?: string
  readonly enabled: boolean
  readonly recurrence: Recurrence
  readonly createdAt: Timestamp
  readonly updatedAt: Timestamp
}

// ─── Formatting ───────────────────────────────────────────────────────────────

export function formatTimeOfDay(time: TimeOfDay): string {
  return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`
}

export function compareTimesOfDay(a: TimeOfDay, b: TimeOfDay): number {
  return a.hour * 60 + a.minute - (b.hour * 60 + b.minute)
}

/**
 * A recurrence as a sentence.
 *
 * In core rather than in the component that first needed it, for the same
 * reason `describeTrend` is: the list, the editor's preview and whatever the
 * native app builds must not be able to word the same rule differently.
 */
export function describeRecurrence(recurrence: Recurrence): string {
  switch (recurrence.kind) {
    case 'daily':
      return `Every day at ${listTimes(recurrence.times)}`
    case 'weekly': {
      const days = [...recurrence.weekdays]
        .sort((a, b) => a - b)
        .map((day) => weekdayName(day))
      return `${listOf(days)} at ${listTimes(recurrence.times)}`
    }
    case 'interval':
      return (
        `Every ${recurrence.everyHours === 1 ? 'hour' : `${String(recurrence.everyHours)} hours`}` +
        ` between ${formatTimeOfDay(recurrence.from)} and ${formatTimeOfDay(recurrence.to)}`
      )
  }
}

function listTimes(times: readonly TimeOfDay[]): string {
  return listOf([...times].sort(compareTimesOfDay).map(formatTimeOfDay))
}

function listOf(items: readonly string[]): string {
  if (items.length <= 1) {
    return items[0] ?? ''
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`
}

// ─── Validation ───────────────────────────────────────────────────────────────

const MAX_NAME_LENGTH = 60
const MAX_TIMES = 12
const MIN_INTERVAL_HOURS = 1
const MAX_INTERVAL_HOURS = 12

function validTime(time: TimeOfDay): boolean {
  return (
    Number.isInteger(time.hour) &&
    Number.isInteger(time.minute) &&
    time.hour >= 0 &&
    time.hour <= 23 &&
    time.minute >= 0 &&
    time.minute <= 59
  )
}

function validateTimes(times: readonly TimeOfDay[]): ValidationError[] {
  const errors: ValidationError[] = []
  if (times.length === 0) {
    errors.push(
      invalid('reminder.times.empty', 'A reminder needs at least one time.', 'times'),
    )
  }
  if (times.length > MAX_TIMES) {
    errors.push(
      invalid(
        'reminder.times.tooMany',
        `A reminder can have at most ${String(MAX_TIMES)} times.`,
        'times',
      ),
    )
  }
  if (times.some((time) => !validTime(time))) {
    errors.push(invalid('reminder.times.invalid', 'That is not a time of day.', 'times'))
  }
  const seen = new Set(times.map(formatTimeOfDay))
  if (seen.size !== times.length) {
    // Two identical times would fire once anyway, so the duplicate is a mistake
    // rather than a preference — and silently collapsing it hides a mis-tap.
    errors.push(
      invalid('reminder.times.duplicate', 'That time is listed twice.', 'times'),
    )
  }
  return errors
}

export function validateRecurrence(recurrence: Recurrence): Result<Recurrence> {
  const errors: ValidationError[] = []

  switch (recurrence.kind) {
    case 'daily':
      errors.push(...validateTimes(recurrence.times))
      break
    case 'weekly':
      errors.push(...validateTimes(recurrence.times))
      if (recurrence.weekdays.length === 0) {
        errors.push(
          invalid('reminder.weekdays.empty', 'Choose at least one day.', 'weekdays'),
        )
      }
      if (new Set(recurrence.weekdays).size !== recurrence.weekdays.length) {
        errors.push(
          invalid('reminder.weekdays.duplicate', 'That day is listed twice.', 'weekdays'),
        )
      }
      break
    case 'interval': {
      const { everyHours, from, to } = recurrence
      if (
        !Number.isInteger(everyHours) ||
        everyHours < MIN_INTERVAL_HOURS ||
        everyHours > MAX_INTERVAL_HOURS
      ) {
        errors.push(
          invalid(
            'reminder.interval.invalid',
            `Choose between ${String(MIN_INTERVAL_HOURS)} and ${String(MAX_INTERVAL_HOURS)} hours.`,
            'everyHours',
          ),
        )
      }
      if (!validTime(from) || !validTime(to)) {
        errors.push(
          invalid('reminder.window.invalid', 'That is not a time of day.', 'from'),
        )
      } else if (compareTimesOfDay(from, to) >= 0) {
        // Refused rather than wrapped past midnight. A window that runs into
        // the next day is a different feature, and guessing which one was meant
        // produces reminders at three in the morning.
        errors.push(
          invalid(
            'reminder.window.inverted',
            'The end of the window must be after the start.',
            'to',
          ),
        )
      }
      break
    }
  }

  return errors.length > 0 ? err<Recurrence>(...errors) : ok(recurrence)
}

export interface ValidateReminderOptions {
  readonly existing?: readonly ReminderSchedule[]
}

export function validateReminder(
  schedule: ReminderSchedule,
  options: ValidateReminderOptions = {},
): Result<ReminderSchedule> {
  const errors: ValidationError[] = []
  const name = schedule.name.trim()

  if (name === '') {
    errors.push(invalid('reminder.name.empty', 'A reminder needs a name.', 'name'))
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.push(
      invalid(
        'reminder.name.tooLong',
        `A name can be at most ${String(MAX_NAME_LENGTH)} characters.`,
        'name',
      ),
    )
  }

  const clash = (options.existing ?? []).some(
    (other) =>
      other.id !== schedule.id && other.name.trim().toLowerCase() === name.toLowerCase(),
  )
  if (name !== '' && clash) {
    errors.push(
      invalid(
        'reminder.name.duplicate',
        `You already have a reminder called "${name}".`,
        'name',
      ),
    )
  }

  const recurrence = validateRecurrence(schedule.recurrence)
  if (!recurrence.ok) {
    errors.push(...recurrence.errors)
  }

  return errors.length > 0 ? err<ReminderSchedule>(...errors) : ok(schedule)
}

// ─── When it next fires ───────────────────────────────────────────────────────

/** How far ahead to look. A weekly schedule always lands within seven days. */
const SEARCH_DAYS = 8

/** The wall-clock times this recurrence produces on one local day. */
export function timesOn(recurrence: Recurrence, day: WallClock): TimeOfDay[] {
  switch (recurrence.kind) {
    case 'daily':
      return [...recurrence.times].sort(compareTimesOfDay)
    case 'weekly':
      return recurrence.weekdays.includes(weekdayOf(day))
        ? [...recurrence.times].sort(compareTimesOfDay)
        : []
    case 'interval': {
      const times: TimeOfDay[] = []
      const end = recurrence.to.hour * 60 + recurrence.to.minute
      for (
        let minutes = recurrence.from.hour * 60 + recurrence.from.minute;
        minutes <= end;
        minutes += recurrence.everyHours * 60
      ) {
        times.push({ hour: Math.floor(minutes / 60), minute: minutes % 60 })
      }
      return times
    }
  }
}

function addDays(wall: WallClock, days: number): WallClock {
  const shifted = new Date(Date.UTC(wall.year, wall.month - 1, wall.day + days))
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: 0,
    minute: 0,
    second: 0,
  }
}

/**
 * The next instant this schedule fires after `after`, in `timeZone`.
 *
 * Built by walking **local days** and converting each day's wall-clock times to
 * instants — not by adding twenty-four hours to the last one. Adding a day of
 * milliseconds is the bug this whole design avoids: it drifts by an hour at
 * every transition and never comes back.
 *
 * What that gives at each transition, which is `epochFromWallClock`'s behaviour
 * and is written out exactly beside that function:
 *
 *   - **Spring forward.** A reminder set inside the abolished hour still fires
 *     that morning, shifted forward by the length of the gap — 01:30 arrives at
 *     what the clock now calls 02:30. It is not skipped: the user asked to be
 *     reminded that day, and an hour of clock change is not a reason to drop
 *     it. Every reminder outside the gap keeps its wall-clock time exactly.
 *   - **Autumn back.** A reminder inside the repeated hour fires **once**, at
 *     the second reading of it. Candidates come from the day's list of times
 *     rather than from scanning instants, so one entry produces one instant —
 *     firing twice is not reachable.
 *
 * The one place that needs an explicit guard is the spring-forward gap making
 * two *different* times identical — see the dedup below.
 *
 * `null` when the schedule is disabled, or has no valid time at all.
 */
export function nextOccurrence(
  schedule: ReminderSchedule,
  after: Timestamp,
  timeZone: string,
): Timestamp | null {
  if (!schedule.enabled) {
    return null
  }

  const today = wallClockIn(after.epochMs, timeZone)

  for (let offset = 0; offset < SEARCH_DAYS; offset += 1) {
    const day = addDays(today, offset)
    const candidates = timesOn(schedule.recurrence, day)
      .map((time) =>
        epochFromWallClock(
          { ...day, hour: time.hour, minute: time.minute, second: 0 },
          timeZone,
        ),
      )
      /*
       * On the morning the clocks go forward, a time inside the gap and the
       * time it shifts onto are the same instant — in London, 01:30 and 02:30
       * both resolve to 01:30Z. A reminder set at both would otherwise fire
       * twice at the identical moment, which is not a reminder, it is a bug
       * with a duplicate.
       */
      .filter((epochMs, index, all) => all.indexOf(epochMs) === index)
      .filter((epochMs) => epochMs > after.epochMs)
      .sort((a, b) => a - b)

    const first = candidates[0]
    if (first !== undefined) {
      return { epochMs: first, timeZone }
    }
  }

  return null
}

/** Every instant this schedule fires in a window, for testing and previews. */
export function occurrencesBetween(
  schedule: ReminderSchedule,
  from: Timestamp,
  toEpochMs: number,
  timeZone: string,
): Timestamp[] {
  const found: Timestamp[] = []
  let cursor = from

  // Bounded rather than `while (true)`: a schedule that somehow produced
  // instants at or before its own cursor would otherwise spin forever, and a
  // preview is never worth an unresponsive tab.
  for (let guard = 0; guard < 1_000; guard += 1) {
    const next = nextOccurrence(schedule, cursor, timeZone)
    if (next === null || next.epochMs > toEpochMs) {
      return found
    }
    found.push(next)
    cursor = next
  }
  return found
}
